import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';
import { invoiceCircleUrl } from './invoiceCircleUrl';
import { parseBlobErrorBody } from '../blobErrorBody';

/**
 * Server-side invoice issuance (#1637, WP2) and corrections (#1643, WP3).
 *
 * Issue, correct and read — there is deliberately no update and no delete
 * endpoint to call, because an issued invoice is append-only (GoBD). A
 * correction is a POST that creates NEW documents (a Storno, and optionally a
 * replacement); the original is never edited. If an update or delete ever
 * appears here, something upstream has gone wrong.
 */

export interface IssuanceReadiness {
    ready: boolean;
    /**
     * Which precondition is unmet: `archive_bucket` | `renderer` (the
     * environment) · `number_circle` | `circle_unusable` (the tenant's
     * numbering circle) · `seller_incomplete` | `einvoice_seller_identifier` |
     * `einvoice_unavailable` (the tenant's legal notice, the build) ·
     * `bank_details` (no IBAN while the named booking's invoice would state
     * an open amount, so it could not say where to pay) ·
     * `tenant` (unresolvable from the request).
     */
    missing: string[];
}

/** One document in a booking's invoice chain (#1643). */
export interface InvoiceDocument {
    id: string;
    number: string;
    /** `invoice` | `cancellation` (the Storno). */
    documentType: string;
    /** `issued` | `corrected` | `canceled` — the latter two: superseded. */
    status: string;
    correctsInvoiceId: string | null;
    issuedAt: string;
    currency: string;
    /** Signed: a cancellation is negative, so the chain sums to what is owed. */
    totalGross: string;
    totalNet: string | null;
    totalTax: string | null;
    pdfSha256: string | null;
    retentionUntil: string | null;
}

/** Pre-fill for the send dialog. `source` labels where the address came from. */
export interface DispatchRecipients {
    to: string[];
    source: 'organisation' | 'tenant_dispatch' | 'booking_customer' | 'manual';
}

/** A buyer field the check step can flag as differing from the organisation. */
export type InvoiceRecipientField = 'name' | 'street' | 'postalCode' | 'city' | 'country';

/**
 * Who an invoice for a booking will be addressed to — read on the server
 * exactly as issuance reads it (ADR-0012 amendment 2026-10-12).
 */
export interface InvoiceRecipient {
    buyer: {
        name: string | null;
        street: string | null;
        postalCode: string | null;
        city: string | null;
        /** ISO 3166-1 alpha-2. */
        country: string | null;
        /** Set when the invoice goes to a public body as XRechnung. */
        leitwegId?: string | null;
        /** The person printed under a company's name (#3427). */
        contactPerson?: string | null;
    };
    costCenter: string | null;
    /** The organisation the booking is attributed to, or null. */
    organization: {
        id: string;
        legalName: string;
        street: string | null;
        postalCode: string | null;
        city: string | null;
        country: string | null;
    } | null;
    /** Buyer fields that differ from the organisation's master data. */
    differences: InvoiceRecipientField[];
}

/**
 * Whose invoices the list covers (ADR-0012). Every list answer carries it, so
 * an empty list says WHY it is empty: `kolula` lists what kolula issued,
 * `external` / null mean the tenant's own system issues and the list is
 * expected to be empty.
 */
export interface InvoiceListScope {
    /** `kolula` | `external` | null (unset). A legacy store answers 409 `legacy_store` instead. */
    invoiceIssuer: string | null;
    /** English, written for API readers; the UI shows its own text per `invoiceIssuer`. */
    statement: string;
}

/**
 * One document in the tenant's invoice index (`GET /admin/invoice`, #1644).
 *
 * Pinned through the real handler and a JSON round trip in
 * `new-backend/admin/src/controllers/invoiceController.test.ts` (and against a
 * real Postgres in `cancellationFeeInvoice.probe.test.ts`): the totals are
 * NUMBERS on this endpoint — unlike the per-booking chain above, whose totals
 * are strings.
 */
export interface InvoiceListEntry {
    id: string;
    number: string;
    /** The number circle's key (`default` unless the tenant runs several). */
    numberRange: string;
    seriesYear: number | null;
    sequence: number;
    /** `invoice` | `cancellation` (the Storno). */
    documentType: string;
    /** EN 16931 BT-3: `380` invoice, `384` cancellation. */
    typeCode: string;
    /** `issued` | `corrected` | `canceled` — the latter two: superseded. */
    status: string;
    /** The number of the document this one cancels or replaces. */
    correctsNumber: string | null;
    /** ISO instant. */
    issuedAt: string;
    orderId: string;
    bookingNumber: string | null;
    currency: string;
    /** Signed: a cancellation is negative. */
    totalGross: number;
    totalNet: number | null;
    totalTax: number | null;
    /** `YYYY-MM-DD`. */
    retentionUntil: string | null;
    pdfSha256: string | null;
}

export interface InvoiceListPage {
    scope: InvoiceListScope;
    period: { from: string, to: string };
    /** Chronological — issued_at, then id — the order a ledger is read in. */
    invoices: InvoiceListEntry[];
    /** Opaque; null when the period is exhausted. */
    nextCursor: string | null;
}

/** Local (Europe/Berlin) days, both inclusive, `YYYY-MM-DD`. */
export interface InvoiceListPeriod {
    from: string;
    to: string;
}

/** Rows per request. The server caps a page at 500. */
export const INVOICE_LIST_PAGE_SIZE = 100;

export interface InvoiceDocumentChain {
    documents: InvoiceDocument[];
    /** Σ totalGross, computed in cents on the server. */
    netGross: string;
}

export interface InvoiceCircle {
    id: string;
    key: string;
    prefix: string;
    formatTemplate: string;
    seqPadding: number;
    nextSeq: number;
    resetYearly: boolean;
    currentYear: number | null;
    /**
     * How many invoices this circle has issued. Non-zero means it is frozen:
     * the prefix, template and counter can no longer be changed, because
     * renumbering issued invoices is not permitted.
     */
    issuedCount: number;
}

export const invoiceApiSlice = createApi({
    reducerPath: 'invoiceApi',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['Invoice', 'InvoiceCircle'],
    // `Invoice` carries the order id for one booking's chain, and the id
    // `LIST` for the tenant's invoice list: issuing or correcting changes both.
    endpoints: (builder) => ({
        // Readiness now depends on the tenant's numbering circle, so seeding
        // one has to clear this gate: without the tag the operator saves the
        // circle and the Issue button stays hidden until a reload. The tenant
        // itself travels in `X-Tenant` and is already in the cache key via
        // `serializeQueryArgsWithTenant`, so no argument is needed.
        //
        // The booking on screen is passed as `orderId`: whether its invoice
        // needs the bank details depends on whether it states an open amount
        // (PO, 2026-10-19), so `bank_details` is answered per booking.
        getIssuanceReadiness: builder.query<IssuanceReadiness, string | void>({
            query: (orderId) => ({
                url: '/invoice/readiness',
                method: 'GET',
                ...(orderId ? { params: { orderId } } : {}),
            }),
            providesTags: ['InvoiceCircle'],
        }),

        getInvoiceDocuments: builder.query<InvoiceDocumentChain, string>({
            query: (orderId) => ({ url: `/invoice/order/${orderId}/documents`, method: 'GET' }),
            providesTags: (_r, _e, orderId) => [{ type: 'Invoice', id: orderId }],
        }),

        /**
         * One page of the tenant's invoice list for a period
         * (`GET /admin/invoice`, #1644). `/invoice` and not `/admin/invoice`:
         * the new-backend base URL already ends in `/admin`. Without `cursor`
         * the first page; with it, the page after the one that returned it.
         */
        listInvoices: builder.query<InvoiceListPage, InvoiceListPeriod & { cursor?: string }>({
            query: ({ from, to, cursor }) => ({
                url: '/invoice',
                method: 'GET',
                params: { from, to, limit: INVOICE_LIST_PAGE_SIZE, ...(cursor ? { cursor } : {}) },
            }),
            providesTags: [{ type: 'Invoice', id: 'LIST' }],
        }),

        correctInvoice: builder.mutation<
            { cancellation: { invoiceId: string, number: string }, replacement: { invoiceId: string, number: string } | null },
            {
                invoiceId: string;
                orderId: string;
                mode: 'cancel' | 'replace';
                language?: 'de' | 'en';
                /** Printed on the Storno (cancel) or on the replacement invoice (replace). */
                note?: string;
                /** The replacement invoice's due date (`YYYY-MM-DD`); absent = the tenant's Zahlungsziel. */
                dueDate?: string;
                /** #3421: the replacement's price adjustment as a row (true) or folded (false); absent = a row. */
                showPriceAdjustment?: boolean;
            }
        >({
            query: ({ invoiceId, mode, language, note, dueDate, showPriceAdjustment }) => ({
                url: `/invoice/${invoiceId}/correction`,
                method: 'POST',
                data: { mode, language, note, dueDate, showPriceAdjustment },
            }),
            invalidatesTags: (_r, _e, { orderId }) => [{ type: 'Invoice', id: orderId }, { type: 'Invoice', id: 'LIST' }],
        }),

        issueInvoice: builder.mutation<
            { invoiceId: string, number: string, pdfSha256: string },
            { orderId: string, language?: 'de' | 'en', note?: string, dueDate?: string, showPriceAdjustment?: boolean }
        >({
            query: ({ orderId, language, note, dueDate, showPriceAdjustment }) => ({
                url: `/invoice/order/${orderId}`,
                method: 'POST',
                data: { language, note, dueDate, showPriceAdjustment },
            }),
            invalidatesTags: (_r, _e, { orderId }) => [{ type: 'Invoice', id: orderId }, { type: 'Invoice', id: 'LIST' }],
        }),

        /**
         * The archived PDF, as bytes.
         *
         * Through the authenticated client and NOT a `window.open` on the
         * URL: the endpoint sits behind `authMiddleware` and needs both the
         * bearer token and `x-tenant`, neither of which a plain navigation
         * sends. It would 401, and a 401 that arrives as a blank tab reads as
         * a broken document rather than as a missing header.
         */
        downloadInvoicePdf: builder.query<Blob, string>({
            query: (invoiceId) => ({
                url: `/invoice/${invoiceId}/pdf`,
                method: 'GET',
                responseType: 'blob',
            }),
            // The refusal arrives as a Blob too; read it back so `messageOf`
            // finds its `reason` (see `blobErrorBody.ts`).
            transformErrorResponse: parseBlobErrorBody,
        }),

        /**
         * The document issuing WOULD produce, watermarked and stored nowhere.
         *
         * Through the authenticated client for the same reason as the PDF
         * above: a plain navigation sends neither the bearer token nor
         * `x-tenant`, so it would 401 into a blank tab.
         *
         * Not cached and not tagged: a preview is a question about the booking
         * as it is right now, and a stale answer is the one thing it must
         * never give — the operator is about to commit on the strength of it.
         */
        previewInvoice: builder.query<Blob, { orderId: string, language: 'de' | 'en', note?: string, dueDate?: string, showPriceAdjustment?: boolean }>({
            query: ({ orderId, language, note, dueDate, showPriceAdjustment }) => ({
                url: `/invoice/order/${orderId}/preview`,
                method: 'GET',
                // Only what is set: a blank note means none, an absent due
                // date the tenant's Zahlungsziel.
                params: {
                    language,
                    ...(note?.trim() ? { note } : {}),
                    ...(dueDate ? { dueDate } : {}),
                    ...(showPriceAdjustment === undefined ? {} : { showPriceAdjustment: String(showPriceAdjustment) }),
                },
                responseType: 'blob',
            }),
            // The refusal arrives as a Blob too; read it back so `messageOf`
            // finds its `reason` (see `blobErrorBody.ts`).
            transformErrorResponse: parseBlobErrorBody,
            keepUnusedDataFor: 0,
        }),

        /**
         * Who the send dialog should offer, and which precedence level it came
         * from. A suggestion only — the operator may change it and the POST
         * re-validates whatever comes back.
         */
        // Always fresh: the admin may just have corrected the booking, and a
        // cached answer would show the old address on the very check meant to
        // catch it.
        getInvoiceRecipient: builder.query<InvoiceRecipient, string>({
            query: (orderId) => ({ url: `/invoice/order/${orderId}/recipient`, method: 'GET' }),
            keepUnusedDataFor: 0,
        }),

        getDispatchRecipients: builder.query<DispatchRecipients, string>({
            query: (invoiceId) => ({ url: `/invoice/${invoiceId}/recipients`, method: 'GET' }),
            keepUnusedDataFor: 0,
        }),

        /**
         * Mail an issued invoice. Re-sending is allowed on purpose, so this
         * invalidates nothing and flips no flag — the server appends an event
         * per attempt.
         */
        dispatchInvoice: builder.mutation<
            { number: string, to: string[] },
            { invoiceId: string, to: string[], cc?: string[], source: string }
        >({
            query: ({ invoiceId, to, cc, source }) => ({
                url: `/invoice/${invoiceId}/dispatch`,
                method: 'POST',
                data: { to, ...(cc && cc.length ? { cc } : {}), source },
            }),
        }),

        /**
         * The GoBD Z3 audit export (#1644) as a ZIP, for local days `from`–`to`
         * inclusive. Owner or master only, enforced by the server.
         */
        downloadAuditExport: builder.query<Blob, { from: string, to: string }>({
            query: ({ from, to }) => ({
                url: '/invoice/audit-export',
                method: 'GET',
                params: { from, to },
                responseType: 'blob',
            }),
            // The refusal arrives as a Blob too; read it back so `messageOf`
            // finds its `reason` (see `blobErrorBody.ts`).
            transformErrorResponse: parseBlobErrorBody,
        }),

        // `slug` addresses another tenant as a master. kolula staff operate
        // production as masters with NO tenant of their own, so the
        // own-tenant route answers 404 `account_no_tenant` for them — the
        // circle was unreachable for every live tenant until this existed.
        getInvoiceCircle: builder.query<{ circle: InvoiceCircle | null }, string | undefined>({
            query: (slug) => ({ url: invoiceCircleUrl(slug), method: 'GET' }),
            providesTags: ['InvoiceCircle'],
        }),

        // `slug` addresses another tenant as a master, exactly as the GET above
        // does. It is carried in the URL and kept OUT of the body, which is the
        // tenant's own field set.
        //
        // The master half of this pair was missed: the backend route and the
        // caller's `slug` both landed, but this mutation still posted to the
        // own-tenant URL and its arg type had no `slug`, so a master's slug was
        // silently dropped into the body and the request 404'd
        // `account_no_tenant`. tsc could not see it — the call site spreads
        // `...(masterSlug ? { slug: masterSlug } : {})`, and a spread bypasses
        // excess-property checking.
        saveInvoiceCircle: builder.mutation<
            { circle: InvoiceCircle },
            {
                slug?: string;
                prefix: string;
                formatTemplate?: string;
                nextSeq?: number;
                seqPadding?: number;
                resetYearly?: boolean;
            }
        >({
            query: ({ slug, ...data }) => ({
                url: invoiceCircleUrl(slug),
                method: 'PUT',
                data,
            }),
            invalidatesTags: ['InvoiceCircle'],
        }),
    }),
});

export const {
    useGetIssuanceReadinessQuery,
    useGetInvoiceDocumentsQuery,
    useListInvoicesQuery,
    useLazyListInvoicesQuery,
    useIssueInvoiceMutation,
    useCorrectInvoiceMutation,
    useLazyDownloadInvoicePdfQuery,
    useLazyPreviewInvoiceQuery,
    useLazyGetDispatchRecipientsQuery,
    useGetInvoiceRecipientQuery,
    useDispatchInvoiceMutation,
    useLazyDownloadAuditExportQuery,
    useGetInvoiceCircleQuery,
    useSaveInvoiceCircleMutation,
} = invoiceApiSlice;
